import ExperimentSummary, { type SummaryData } from '../ExperimentSummary';
import { FlowContainer, FlowNode, FlowArrow, FlowTerminal, FlowNote, FlowParallel } from '../FlowPrimitives';

// ============================================================================
// Experiment 5 – Soaps and Detergents
// ============================================================================

const summaryData: SummaryData = {
  experimentId: 'exp5',
  title: 'Preparation and Properties of Soaps and Detergents',
  objectives: [
    'Synthesize soap via saponification of a fat',
    'Prepare a synthetic detergent from dodecanol',
    'Compare properties of soap vs. detergent',
    'Understand surfactant chemistry (hydrophilic head, hydrophobic tail)',
  ],
  safetyNotes: [
    'Wear eye protection at all times',
    'The alcoholic NaOH solution is highly caustic – burns skin and damages clothing; wash immediately with copious water if contacted',
    'Concentrated H₂SO₄ is highly corrosive – add slowly while stirring in the fume hood',
    'Dodecanol is a non-halogenated solvent – dispose of appropriately',
    'Na₂CO₃ neutralization produces CO₂ gas (effervescence)',
  ],
  equipment: [
    'Large beaker for boiling water bath',
    'Hot plate',
    '125 mL flask (for saponification)',
    'Watch glass (to cover flask)',
    'Boiling chips',
    'Graduated cylinder',
    'Cheesecloth (several layers, for soap filtration)',
    '400 mL beaker (for salt solution)',
    '100 mL beaker (for detergent)',
    '250 mL beaker (for ice-salt bath)',
    'Glass stirring rod',
    'Vacuum filtration apparatus (for detergent)',
    'Test tubes (6-inch / 15 cm) and corks',
    'Broad- and narrow-range indicator paper',
  ],
  chemicals: [
    'Lard or vegetable oil (5.0 g)',
    '15% NaOH alcohol stock solution (30.0 mL)',
    'NaCl (25.0 g, dissolved in 150 mL water)',
    'Dodecanol (C₁₂H₂₅OH, 5.0 mL)',
    'Concentrated H₂SO₄ (2.0 mL)',
    '1 M Na₂CO₃ (15.0 mL)',
    'Phenolphthalein indicator (4 drops)',
    'Ice + salt (10 g, for ice-salt bath)',
    'Ice-cold water',
    'Liquid shortening or vegetable oil (for emulsification test)',
    '1% CaCl₂, 1% MgCl₂, 1% FeCl₃ (for hard water test)',
    'Dilute (1 M) HCl',
  ],
  procedure: [
    {
      number: 1,
      title: 'Part I – Prepare Soap',
      details: [
        'Set up boiling water bath (large beaker, boiling chips, hot plate)',
        'Place 5.0 g lard or vegetable oil into a 125 mL flask',
        'Add 30.0 mL of 15% NaOH alcohol stock solution, add boiling chips, cover with watch glass',
        'Suspend flask 2 mm above boiling water for 40 minutes',
        'Test for completeness – no visible oil droplets should remain',
      ],
      safetyNote: 'Alcoholic NaOH is highly caustic; hot water bath presents burn hazard',
      manualRef: 'Part I',
    },
    {
      number: 2,
      title: 'Part I – Isolate Soap (Salting Out)',
      details: [
        'Dissolve 25.0 g NaCl in 150 mL water in a 400 mL beaker (set ~25 mL aside for rinsing)',
        'Pour hot mixture into NaCl solution, rinse flask with reserved solution',
        'Stir for several minutes, strain through several layers of cheesecloth',
        'Wash collected soap twice with 10 mL portions of ice-cold water',
        'Twist cheesecloth to remove excess water; describe appearance, colour, texture, odour',
      ],
      manualRef: 'Part I',
    },
    {
      number: 3,
      title: 'Part II – Prepare Detergent',
      details: [
        'Place 5.0 mL dodecanol into a 100 mL beaker',
        'Slowly add 2.0 mL concentrated H₂SO₄ in fume hood while stirring; stir 1 min, stand 10 min',
        'Prepare ice-salt bath: 250 mL beaker, ice, 10 g salt, water to 75 mL',
        'Prepare 15.0 mL of 1 M Na₂CO₃ + 4 drops phenolphthalein in a small beaker',
        'Slowly pour Na₂CO₃ into dodecanol mixture; stir until pink colour disappears',
        'Pour into ice-salt bath, stir vigorously; filter by vacuum filtration',
        'Wash twice with 10 mL ice-cold water',
      ],
      safetyNote: 'Conc. H₂SO₄ is extremely corrosive – add slowly in fume hood',
      manualRef: 'Part II',
    },
    {
      number: 4,
      title: 'Part III – Emulsification Test',
      details: [
        'Add 5 mL water + 5 drops oil to two test tubes',
        'Shake and observe separation',
        'Add soap solution to one tube, detergent to the other',
        'Shake and compare emulsion stability',
      ],
      manualRef: 'Part III – Test 1',
    },
    {
      number: 5,
      title: 'Part III – Hard Water Test',
      details: [
        'Add 5 mL hard water (Ca\u00B2⁺/Mg\u00B2⁺) to two test tubes',
        'Add soap solution to one – observe precipitate (scum)',
        'Add detergent to the other – observe no precipitate',
      ],
      manualRef: 'Part III – Test 2',
    },
    {
      number: 6,
      title: 'Part III – pH & Acid Tests',
      details: [
        'Measure pH: soap solution \u2248 9–10; detergent \u2248 7–8',
        'Add HCl to soap solution – observe oily precipitate',
        'Add HCl to detergent – observe no change',
      ],
      manualRef: 'Part III – Tests 3 & 4',
    },
  ],
  keyFormulas: [
    'Fat + 3NaOH → Glycerol + 3 Soap (sodium salt of fatty acid)',
    'Dodecanol + H₂SO₄ → Dodecyl hydrogen sulfate',
    'Dodecyl hydrogen sulfate + Na₂CO₃ → Sodium dodecyl sulfate + CO₂ + H₂O',
  ],
  tips: [
    'Suspend flask 2 mm above boiling water – lower as water level falls',
    'The soap should be smooth with no oil droplets before salting out',
    'Add conc. H₂SO₄ very slowly (2.0 mL total) – the reaction is exothermic',
    'Pink colour disappearing from phenolphthalein confirms neutralization is complete',
    'Use 4 drops of phenolphthalein for best visibility',
    'Compare soap vs. detergent properties side by side for clearest results',
    'Record observations immediately – emulsions may break over time',
  ],
};

// ============================================================================
// Flowchart
// ============================================================================

function Flowchart() {
  // Layout constants
  const cx = 210;          // center x for main nodes
  const nodeW = 180;
  const nodeH = 56;
  const termW = 140;
  const termH = 36;

  // Left branch (soap) and right branch (detergent) for Part I/II
  const leftX = 60;
  const rightX = 360;
  const branchNodeW = 170;

  // Vertical positions
  const startY = 40;
  const splitY = 100;
  const b1Y = 170;        // branch step 1
  const b2Y = 250;        // branch step 2
  const mergeY = 340;
  const testY = 420;
  const endY = 500;

  return (
    <FlowContainer width={600} height={560} title="Soaps & Detergents Procedure Flow">
      {/* Start */}
      <FlowTerminal x={cx + (nodeW - termW) / 2} y={startY} label="Start" />

      {/* Arrow: Start -> Split node */}
      <FlowArrow
        from={{ x: cx + nodeW / 2, y: startY + termH }}
        to={{ x: cx + nodeW / 2, y: splitY }}
      />

      {/* Split node */}
      <FlowNode x={cx} y={splitY} title="Experiment Setup" subtitle="3-part experiment" color="#6366f1" />

      {/* Arrow: split -> left branch (Soap) */}
      <FlowArrow
        from={{ x: cx, y: splitY + nodeH / 2 }}
        to={{ x: leftX + branchNodeW, y: b1Y + nodeH / 2 }}
        label="Part I"
      />

      {/* Arrow: split -> right branch (Detergent) */}
      <FlowArrow
        from={{ x: cx + nodeW, y: splitY + nodeH / 2 }}
        to={{ x: rightX, y: b1Y + nodeH / 2 }}
        label="Part II"
      />

      {/* Left: Make Soap */}
      <FlowNode x={leftX} y={b1Y} width={branchNodeW} title="Saponification" subtitle="Heat fat + NaOH 40 min" color="#0d9488" />

      {/* Left: Salt out */}
      <FlowArrow
        from={{ x: leftX + branchNodeW / 2, y: b1Y + nodeH }}
        to={{ x: leftX + branchNodeW / 2, y: b2Y }}
      />
      <FlowNode x={leftX} y={b2Y} width={branchNodeW} title="Salt Out & Filter" subtitle="NaCl precipitation" color="#0d9488" />

      {/* Right: Sulfonate */}
      <FlowNode x={rightX} y={b1Y} width={branchNodeW} title="Sulfonate Dodecanol" subtitle="Add H₂SO₄ drop by drop" color="#2563eb" />

      {/* Right: Neutralize */}
      <FlowArrow
        from={{ x: rightX + branchNodeW / 2, y: b1Y + nodeH }}
        to={{ x: rightX + branchNodeW / 2, y: b2Y }}
      />
      <FlowNode x={rightX} y={b2Y} width={branchNodeW} title="Neutralize & Crystallize" subtitle="Na₂CO₃ until pink" color="#2563eb" />

      {/* Safety notes */}
      <FlowNote x={leftX} y={b1Y - 30} text="Burn hazard – hot bath" width={branchNodeW} />
      <FlowNote x={rightX} y={b1Y - 30} text="H₂SO₄ – ice bath required" width={branchNodeW} />

      {/* Arrows: both branches merge */}
      <FlowArrow
        from={{ x: leftX + branchNodeW / 2, y: b2Y + nodeH }}
        to={{ x: cx + nodeW / 2, y: mergeY }}
      />
      <FlowArrow
        from={{ x: rightX + branchNodeW / 2, y: b2Y + nodeH }}
        to={{ x: cx + nodeW / 2, y: mergeY }}
      />

      {/* Merge: Part III */}
      <FlowNode x={cx} y={mergeY} title="Part III: Compare" subtitle="Test both products" color="#7c3aed" />

      {/* Arrow to tests */}
      <FlowArrow
        from={{ x: cx + nodeW / 2, y: mergeY + nodeH }}
        to={{ x: cx + nodeW / 2, y: testY }}
      />

      {/* Property tests (parallel) */}
      <FlowParallel
        x={cx - 10}
        y={testY}
        width={200}
        items={['Emulsification', 'Hard Water', 'pH Test', 'Acid Reaction']}
        color="#7c3aed"
      />

      {/* Arrow to End */}
      <FlowArrow
        from={{ x: cx + nodeW / 2, y: testY + 4 * 28 + 8 }}
        to={{ x: cx + nodeW / 2, y: endY }}
      />

      {/* End */}
      <FlowTerminal x={cx + (nodeW - termW) / 2} y={endY} label="End" color="#ef4444" />
    </FlowContainer>
  );
}

// ============================================================================
// Export
// ============================================================================

export default function Exp5Summary() {
  return <ExperimentSummary data={summaryData} flowchart={<Flowchart />} />;
}
